import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from "react";
import * as THREE from "three";
import { ThreeEvent, useThree, invalidate } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { TextType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { BoundingBox, SelectableSymbol } from "./SelectableSymbol";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { Html, Text } from "@react-three/drei";
import { editorStore } from "../../store/editorStore";
import { renderStore } from "../../store/renderStore";
import { observer } from "mobx-react-lite";
import { createDoubleClickDetector } from "../../utils/doubleClick";

interface TextSymbolProps {
  text: TextType;
  onDragStart: (text: TextType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

/**
 * TextSymbol - Renders an editable text object on the floor plan.
 * 
 * Uses TroikaText (SDF-based) for crisp rendering at any zoom level.
 * Supports:
 * - Word wrapping within the selection bounding box
 * - Font size, family, and alignment customization
 * - In-place editing with WYSIWYG sizing
 * - Selection box resize updates text wrap boundaries
 */
const TextSymbol: React.FC<TextSymbolProps> = observer(({
  text,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const { camera, gl } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Lenient double-click: native dblclick fails with high macOS mouse speed. */
  const doubleClickDetectorRef = useRef(createDoubleClickDetector());
  const floorplannerStore = useContext(FloorplannerStoreContext);

  // Track editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [editText, setEditText] = useState(text.text || floorplannerStore.text);
  const [localPosition, setLocalPosition] = useState<[number, number]>([text.position.x, text.position.y]);
  const [localScaleX, setLocalScaleX] = useState(text.textBoxWidth ?? 1);
  const [localScaleY, setLocalScaleY] = useState(text.textBoxHeight ?? 1);
  const [localWrapWidth, setLocalWrapWidth] = useState(text.wrapWidth);

  // Track text bounds for bounding box calculation
  const [textBounds, setTextBounds] = useState<{ width: number; height: number } | null>(null);

  // Use text properties from the symbol, with fallbacks
  // textBoxWidth/Height represent the SCALE of the text object (1 = original size)
  const scaleX = localScaleX;
  const scaleY = localScaleY;
  const wrapWidth = localWrapWidth; // Optional word wrap width
  const fontSize = text.fontSize ?? 0.5;
  const fontFamily = text.fontFamily ?? "sans-serif";
  const fontWeight = text.fontWeight ?? "normal";
  const textAlign = text.textAlign ?? "left";
  const lineHeight = text.lineHeight ?? 1.2;

  // Sync editText when text.text changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditText(text.text || floorplannerStore.text);
    }
  }, [text.text, floorplannerStore.text, isEditing]);

  useEffect(() => {
    if (isResizing) return;
    setLocalPosition([text.position.x, text.position.y]);
    setLocalScaleX(text.textBoxWidth ?? 1);
    setLocalScaleY(text.textBoxHeight ?? 1);
    setLocalWrapWidth(text.wrapWidth);
  }, [isResizing, text.position.x, text.position.y, text.textBoxWidth, text.textBoxHeight, text.wrapWidth]);

  // Calculate CSS font size for HTML overlay to match 3D rendering
  // This ensures WYSIWYG editing
  const cssPixelScale = useMemo(() => {
    // Get the camera's zoom level
    const orthoCamera = camera as THREE.OrthographicCamera;
    const zoom = orthoCamera.zoom || 1;

    // Convert world units to pixels based on viewport
    // fontSize is in world units, we need to convert to CSS pixels
    // Higher zoom = larger apparent size, so we multiply
    const basePixelsPerUnit = gl.domElement.clientHeight /
      (orthoCamera.top - orthoCamera.bottom);

    return fontSize * basePixelsPerUnit * zoom;
  }, [camera, gl.domElement.clientHeight, fontSize]);

  // Calculate textarea dimensions in CSS pixels
  // The textarea should match the scaled rendered text size
  const textareaStyle = useMemo(() => {
    const orthoCamera = camera as THREE.OrthographicCamera;
    const zoom = orthoCamera.zoom || 1;
    const basePixelsPerUnit = gl.domElement.clientHeight /
      (orthoCamera.top - orthoCamera.bottom);

    // Use measured text bounds if available, otherwise default
    const measuredWidth = textBounds?.width ?? 2;
    const measuredHeight = textBounds?.height ?? 1;

    // Apply uniform scale to font size and dimensions
    const uniformScale = Math.max(scaleX, scaleY);
    const scaledFontSize = fontSize * uniformScale;
    const fontSizePx = scaledFontSize * basePixelsPerUnit * zoom;

    // Width/height based on measured text bounds
    const widthPx = measuredWidth * uniformScale * basePixelsPerUnit * zoom;
    const heightPx = measuredHeight * uniformScale * basePixelsPerUnit * zoom;

    return {
      width: `${widthPx}px`,
      height: `${heightPx}px`,
      fontSize: `${fontSizePx}px`,
      fontFamily: fontFamily,
      fontWeight: fontWeight === "bold" ? "bold" : "normal",
      textAlign: textAlign as "left" | "center" | "right",
      lineHeight: lineHeight,
      background: "rgba(255, 255, 255, 0.9)",
      border: "1px solid #ccc",
      borderRadius: "4px",
      color: "black",
      outline: "none",
      resize: "none" as const,
      overflow: "hidden" as const,
      padding: "8px",
      margin: 0,
      boxSizing: "border-box" as const,
      whiteSpace: "pre-wrap" as const,
      zIndex: 10,
    };
  }, [camera, gl.domElement.clientHeight, scaleX, scaleY, fontSize, fontFamily, fontWeight, textAlign, lineHeight, textBounds]);

  const handleTextUpdate = useCallback(() => {
    const finalText = editText.trim() === "" ? floorplannerStore.text : editText;
    setEditText(finalText);
    floorplannerStore.updateSymbolProperty(text.id, "text", finalText);
    setIsEditing(false);
    editorStore.setTextEditing(undefined);
    invalidate();
  }, [editText, floorplannerStore, text.id]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      // Cancel editing, revert to original text
      setEditText(text.text || floorplannerStore.text);
      setIsEditing(false);
      editorStore.setTextEditing(undefined);
    }
  }, [text.text, floorplannerStore.text]);

  const handleInputBlur = useCallback(() => {
    handleTextUpdate();
  }, [handleTextUpdate]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditText(event.target.value);
  }, []);

  /**
   * Handle resize from SelectableSymbol
   * - Middle handles (midLeft/midRight): resize bounding box only
   * - Corner handles: scale the text object dimensions
   */
  const handleResize = useCallback((newWidth: number, newHeight: number, boundingBox: BoundingBox, handle: string, newPosition?: [number, number]) => {
    // Use measured text bounds as base - must match calculateBoundingBox logic
    const measuredWidth = textBounds?.width ?? 1;
    const measuredHeight = textBounds?.height ?? 1;
    // Use wrapWidth for width base if set, matching bounding box calculation
    const baseWidth = wrapWidth !== undefined ? wrapWidth : measuredWidth;
    const baseHeight = measuredHeight;

    // Convert bounding box dimensions back to scale values
    const newScaleX = newWidth / baseWidth;
    const newScaleY = newHeight / baseHeight;

    if (handle === "midLeft" || handle === "midRight") {
      // Middle handles: resize word wrap width (in world units)
      const currentUniformScale = Math.max(scaleX, scaleY);
      setLocalWrapWidth(newWidth / currentUniformScale);
    } else {
      // Corner handles: always use uniform scale (same for X and Y)
      // Use the larger of the two to ensure text always scales up proportionally
      const uniformScale = Math.max(newScaleX, newScaleY);
      setLocalScaleX(uniformScale);
      setLocalScaleY(uniformScale);
    }
    if (newPosition) {
      setLocalPosition(newPosition);
    }
    invalidate();
  }, [floorplannerStore, text.id, scaleX, scaleY, textBounds, wrapWidth]);

  /**
   * Handle rotation from SelectableSymbol
   */
  const handleRotate = useCallback((angle: number) => {
    floorplannerStore.updateSymbolProperty(text.id, "rotation", angle);
    invalidate();
  }, [floorplannerStore, text.id]);

  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) {
      return {
        topLeft: [0, 0],
        topRight: [0, 0],
        bottomLeft: [0, 0],
        bottomRight: [0, 0],
        width: 0,
        height: 0,
        depth: 0
      };
    }

    // Bounding box based on actual text bounds and uniform scale
    // Use the same uniform scale logic as handleResize to prevent jumps
    const measuredWidth = textBounds?.width ?? 1;
    const measuredHeight = textBounds?.height ?? 1;
    const uniformScale = Math.max(scaleX, scaleY);
    const width = wrapWidth !== undefined ? wrapWidth * uniformScale : measuredWidth * uniformScale;
    const height = measuredHeight * uniformScale;
    const centerX = width / 2;
    const centerY = height / 2;

    // Return unrotated corners - SelectableSymbol handles rotation via its rotation prop
    return {
      topLeft: [-centerX, centerY],
      topRight: [centerX, centerY],
      bottomLeft: [-centerX, -centerY],
      bottomRight: [centerX, -centerY],
      width,
      height,
      depth: 0
    };
  }, [scaleX, scaleY, wrapWidth, textBounds]);

  useEffect(() => {
    editorStore.updateGroupRef(text.id, groupRef.current);
  }, [text.id, text.selected]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleSingleClick = useCallback((textId: string) => {
    editorStore.setTextEditing(undefined);
    floorplannerStore.selectSymbol(textId);
    editorStore.setSelections([text]);
  }, [floorplannerStore, text]);

  const handleDoubleClick = useCallback((textId: string) => {
    setIsEditing(true);
    editorStore.setTextEditing(textId);
  }, []);

  // Determine anchor position based on textAlign
  const anchorX = useMemo((): 'left' | 'center' | 'right' => {
    return textAlign;
  }, [textAlign]);

  // Calculate offset for text position based on alignment and box size
  // With anchorX/Y at center, we just position at origin
  const textPosition = useMemo((): [number, number, number] => {
    return [0, 0, 0.001];
  }, []);

  return (
    <group position={[0, 0, 0.01 + (text.zIndex * zIndexEpsilon)]}>
      <DraggableObject
        position={localPosition}
        onDragStart={(offset) => onDragStart(text, offset)}
        onDragEnd={(endPosition) => onDragEnd(endPosition)}
        onDrag={onDrag}
        selectable={true}
        attachmentId={text.id}
        attachmentType="noAttachments"
        symbol={text}
      >
        <SelectableSymbol
          ref={groupRef}
          calculateBoundingBox={calculateBoundingBox}
          center={[0, 0]}
          rotation={text.rotation}
          symbol={text}
          drawHandles={!isEditing}
          handleSize={floorplannerStore.symbolHandleSize}
          onResize={handleResize}
          pivot={[0, 0]}
          showMiddleHandles={true}
          showHandles={!isEditing}
          lockAspectRatio={true}
          onResizeStart={() => setIsResizing(true)}
          onResizeEnd={(_event, finalResize) => {
            setIsResizing(false);
            if (!finalResize) return;
            if (finalResize.handle === "midLeft" || finalResize.handle === "midRight") {
              const currentUniformScale = Math.max(scaleX, scaleY);
              floorplannerStore.updateSymbolProperty(text.id, "wrapWidth", finalResize.width / currentUniformScale);
            } else {
              const measuredWidth = textBounds?.width ?? 1;
              const measuredHeight = textBounds?.height ?? 1;
              const baseWidth = wrapWidth !== undefined ? wrapWidth : measuredWidth;
              const baseHeight = measuredHeight;
              const newScaleX = finalResize.width / baseWidth;
              const newScaleY = finalResize.height / baseHeight;
              const uniformScale = Math.max(newScaleX, newScaleY);
              floorplannerStore.updateSymbolProperty(text.id, "textBoxWidth", uniformScale);
              floorplannerStore.updateSymbolProperty(text.id, "textBoxHeight", uniformScale);
            }
            if (finalResize.position) {
              floorplannerStore.updateSymbolProperty(text.id, "position", finalResize.position);
            }
          }}
        >
          <group
            ref={groupRef}
            rotation={[0, 0, text.rotation || 0]}
            onClick={(e: ThreeEvent<MouseEvent>) => {
              // Prefer a custom detector over native dblclick: high tracking
              // speed often moves the cursor past the OS distance threshold.
              if (doubleClickDetectorRef.current.record(e.clientX, e.clientY, text.id)) {
                e.stopPropagation();
                handleDoubleClick(text.id);
                return;
              }
              handleSingleClick(text.id);
            }}
            onDoubleClick={(e: ThreeEvent<MouseEvent>) => {
              // Native fallback when the OS still emits dblclick.
              e.stopPropagation();
              handleDoubleClick(text.id);
            }}
            onPointerEnter={() => {
              if (!isEditing && document.body.style.cursor !== "grabbing") {
                document.body.style.cursor = "move";
              }
            }}
            onPointerLeave={() => {
              if (!isEditing && document.body.style.cursor === "move") {
                document.body.style.cursor = "auto";
              }
            }}
          >
            {/* Text editing overlay - HTML textarea for multi-line editing */}
            {isEditing && (
              <Html center>
                <textarea
                  ref={textareaRef}
                  value={editText}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleInputBlur}
                  autoFocus
                  style={textareaStyle}
                  onFocus={(event) => event.target.select()}
                />
              </Html>
            )}

            {/* Drei Text - SDF rendered text (no auto word wrap) */}
            {!isEditing && (
              <group scale={[Math.max(scaleX, scaleY), Math.max(scaleX, scaleY), 1]}>
                <Text
                  position={textPosition}
                  fontSize={fontSize}
                  color="black"
                  anchorX="center"
                  anchorY="middle"
                  textAlign={textAlign}
                  lineHeight={lineHeight}
                  maxWidth={wrapWidth}
                  renderOrder={2}
                  sdfGlyphSize={renderStore.rendering ? 256 : undefined}
                  onSync={(troika: any) => {
                    if (troika?.material) {
                      troika.material.depthTest = false;
                      troika.material.depthWrite = false;
                      troika.material.transparent = true;
                      troika.material.needsUpdate = true;
                    }
                    // Measure text bounds for bounding box calculation
                    if (troika?.textRenderInfo) {
                      const bounds = troika.textRenderInfo.blockBounds;
                      if (bounds) {
                        const textWidth = bounds[2] - bounds[0];
                        const textHeight = bounds[3] - bounds[1];
                        setTextBounds({ width: textWidth, height: textHeight });
                      }
                    }
                    invalidate();
                  }}
                >
                  {editText}
                </Text>
              </group>
            )}
          </group>
        </SelectableSymbol>
      </DraggableObject>
    </group>
  );
});

export default TextSymbol;
